import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { mark, useI18n } from "../i18n";
import { CONTACT_TOPICS, MESSAGE_MAX, sendContact } from "../lib/contact";
import { Modal } from "./ui";

// The contact form, in a window over the current page (a player reporting
// from a game stays in their room). Messages show up on the owner's
// moderation page. Open it from anywhere: openContact({ topic, room, player }),
// where player ({ id, name }) is someone being reported.
const OPEN_EVENT = "partycade:contact";
export const openContact = (opts = {}) => window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: opts }));

// A link in running text that opens the form.
export const ContactLink = ({ topic, room, children }) => (
  <button type="button" className="link" onClick={() => openContact({ topic, room })}>
    {children}
  </button>
);

const TOPICS = {
  question: mark("A question or feedback"),
  report: mark("Report a problem or a player"),
  privacy: mark("Privacy and your data"),
  legal: mark("Legal (for example the Digital Services Act)"),
};
const looksLikeEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

export default function ContactForm() {
  const { t, lang } = useI18n();
  const { user, playerInfo } = useAuth();
  const [open, setOpen] = useState(null); // { topic, room, player } while open
  const [topic, setTopic] = useState("question");
  const [message, setMessage] = useState(""); // kept if the window is closed by mistake
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null); // null, "sending" or "sent"
  const [error, setError] = useState("");
  const account = user && !user.isAnonymous ? user : null;

  useEffect(() => {
    const onOpen = (e) => {
      const o = e.detail || {};
      setOpen(o);
      setTopic(CONTACT_TOPICS.includes(o.topic) ? o.topic : "question");
      setStatus(null);
      setError("");
      if (account?.email) setEmail((cur) => cur || account.email);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, [account]);

  const close = () => setOpen(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return setError(t("Please write a message."));
    if (email.trim() && !looksLikeEmail(email.trim())) return setError(t("That email address doesn't look right."));
    setError("");
    setStatus("sending");
    try {
      await sendContact({ topic, message, email, name: user ? playerInfo?.name : "", room: open?.room, reported: open?.player, lang });
      setMessage("");
      setStatus("sent");
    } catch (err) {
      console.warn("Couldn't send the message", err);
      setStatus(null);
      setError(t("Couldn't send your message. Please wait a minute and try again."));
    }
  };

  return (
    <Modal open={!!open} onClose={close} title={t("Contact us")} className="contact-modal">
      {status === "sent" ? (
        <div className="contact-sent">
          <h2 className="modal-title">{t("Thanks! Your message was sent.")}</h2>
          <p className="muted">{email.trim() ? t("We'll answer by email.") : t("We read every message.")}</p>
          <button className="btn btn-primary" onClick={close}>
            {t("Close")}
          </button>
        </div>
      ) : (
        <form className="auth-form contact-form" onSubmit={submit}>
          <h2 className="modal-title">{t("Contact us")}</h2>
          <label>
            {t("What's it about?")}
            <select value={topic} onChange={(e) => setTopic(e.target.value)}>
              {CONTACT_TOPICS.map((k) => (
                <option key={k} value={k}>
                  {t(TOPICS[k])}
                </option>
              ))}
            </select>
          </label>
          {open?.player ? (
            <p className="muted small contact-room">{t("Reporting {name} in room {code}", { name: open.player.name, code: open.room })}</p>
          ) : (
            open?.room && <p className="muted small contact-room">{t("About room {code}", { code: open.room })}</p>
          )}
          <label>
            {t("Your message")}
            <textarea
              rows={6}
              maxLength={MESSAGE_MAX}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={topic !== "report" ? "" : open?.player ? t("What did they do?") : t("What happened, and who, if you know their name?")}
              required
            />
          </label>
          <label>
            {t("Your email (optional)")}
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={120} autoComplete="email" />
            <small className="contact-hint">{t("Only if you'd like an answer.")}</small>
          </label>
          {error && <p className="form-error">{error}</p>}
          <button className="btn btn-primary btn-lg" disabled={status === "sending"}>
            {status === "sending" ? t("Sending…") : t("Send message")}
          </button>
          <p className="muted small">{t("We keep your message until we've dealt with it. More in our Privacy Policy.")}</p>
        </form>
      )}
    </Modal>
  );
}
